import { z } from "zod";
import { SortingState } from "@tanstack/react-table";
import { USER_PREFERENCE_KEY_PREFIX } from "@/lib/auth/clearAppStorage";
import { logger } from "@/lib/logger";

/**
 * The sort the user picked on "Dine workspaces", remembered between visits.
 *
 * The list snapped back to `Oprettet` descending the moment you navigated away,
 * so a user who works alphabetically re-sorted on every return (LAR-132).
 *
 * Kept in `localStorage` rather than `sessionStorage` so it also survives a
 * reload and a browser restart, and stamped with the user and tenant that saved
 * it so a shared machine — or a tenant switch — never inherits someone else's
 * sort. One slot, holding whoever saved last: the default is stored as no entry
 * at all (see {@link persistWorkspaceSorting}), so a second user only takes the
 * slot over by sorting, not by opening the page. Signing out still clears it
 * along with everything else, which is what a shared machine wants.
 *
 * Cross-device sync would need a server-side user preference, and is out of
 * scope here.
 */
export const WORKSPACE_SORT_STORAGE_KEY = `${USER_PREFERENCE_KEY_PREFIX}workspace-sort`;

/**
 * The columns the workspace table sorts by. A saved sort naming anything else
 * is stale and falls back to the default; `Columns.test.tsx` fails if the
 * table's own sortable set drifts from this one.
 */
export const WORKSPACE_SORTABLE_COLUMN_IDS = [
  "name",
  "documents",
  "createdAt",
  "updatedAt",
  "owner",
] as const;

/** Newest workspace first — what the list shows until the user says otherwise. */
const DEFAULT_SORT: SortingState[number] = { id: "createdAt", desc: true };

export const DEFAULT_WORKSPACE_SORTING: SortingState = [DEFAULT_SORT];

/** Who the saved sort belongs to. Both halves are required; neither identifies a user on its own. */
export type WorkspaceSortScope = {
  userId: string;
  tenantId: string;
};

/** Bumped when the stored shape changes; older entries fail the parse and are ignored. */
const STORED_VERSION = 1;

const storedSortSchema = z.object({
  version: z.literal(STORED_VERSION),
  scope: z.object({
    userId: z.string().min(1),
    tenantId: z.string().min(1),
  }),
  sort: z.object({
    id: z.enum(WORKSPACE_SORTABLE_COLUMN_IDS),
    desc: z.boolean(),
  }),
});

type StoredSort = z.infer<typeof storedSortSchema>;

const isDefaultSort = (sort: SortingState[number]): boolean =>
  sort.id === DEFAULT_SORT.id && sort.desc === DEFAULT_SORT.desc;

const belongsToScope = (
  saved: StoredSort,
  scope: WorkspaceSortScope
): boolean =>
  saved.scope.userId === scope.userId &&
  saved.scope.tenantId === scope.tenantId;

const forget = (): void => {
  try {
    localStorage.removeItem(WORKSPACE_SORT_STORAGE_KEY);
  } catch {
    // Private mode / quota — see readStoredSort.
  }
};

const readStoredSort = (): StoredSort | null => {
  let raw: string | null = null;
  try {
    raw = localStorage.getItem(WORKSPACE_SORT_STORAGE_KEY);
  } catch {
    // Private mode / storage disabled. Silent, as in the app's other storage
    // helpers (chatScrollMemory, dashboard-skeleton-cache): there is nothing to
    // act on and every read would log it again.
    return null;
  }
  if (!raw) return null;

  let value: unknown = null;
  try {
    value = JSON.parse(raw);
  } catch {
    // Malformed JSON fails the schema below, so both cases are handled once.
  }

  const parsed = storedSortSchema.safeParse(value);
  if (!parsed.success) {
    // Left by an older format, or naming a column the table has since retired.
    // Nothing on this version can restore it, so drop it — that also keeps the
    // warning below to one per entry rather than one per visit.
    logger.warn("Discarding an unreadable saved workspace sort", {
      operation: "readWorkspaceSorting",
      storageKey: WORKSPACE_SORT_STORAGE_KEY,
    });
    forget();
    return null;
  }

  return parsed.data;
};

/**
 * The sort to open the list with: the saved one when it belongs to this user in
 * this tenant and still names a sortable column, the default otherwise.
 */
export const readWorkspaceSorting = (
  scope: WorkspaceSortScope | null
): SortingState => {
  if (!scope) return DEFAULT_WORKSPACE_SORTING;

  const saved = readStoredSort();
  if (!saved || !belongsToScope(saved, scope)) return DEFAULT_WORKSPACE_SORTING;

  return [{ id: saved.sort.id, desc: saved.sort.desc }];
};

/**
 * The line to store for this sort, or `null` for the three cases that mean "no
 * saved sort": the user cleared sorting, the sort is already the default, or
 * the column cannot be restored. Storing the default as an absence is what
 * keeps merely opening the list from taking the single slot away from whoever
 * did save a sort.
 *
 * Only the primary column is kept — the list sorts by one at a time.
 */
const entryToStore = (
  scope: WorkspaceSortScope,
  sorting: SortingState
): string | null => {
  const [primary] = sorting;
  if (!primary || isDefaultSort(primary)) return null;

  const entry = {
    version: STORED_VERSION,
    scope: { userId: scope.userId, tenantId: scope.tenantId },
    sort: { id: primary.id, desc: primary.desc },
  };

  // Validated by the same schema the read side uses, so nothing is written that
  // could not be restored.
  return storedSortSchema.safeParse(entry).success
    ? JSON.stringify(entry)
    : null;
};

/** Saves the user's chosen sort, or forgets the saved one — see {@link entryToStore}. */
export const persistWorkspaceSorting = (
  scope: WorkspaceSortScope | null,
  sorting: SortingState
): void => {
  if (!scope) return;

  const next = entryToStore(scope, sorting);

  if (next === null) {
    const saved = readStoredSort();
    // Someone else's saved sort is not ours to drop.
    if (saved && belongsToScope(saved, scope)) forget();
    return;
  }

  try {
    // Opening the list re-saves what it just restored; skip that write so the
    // entry only churns when the user actually changes the sort.
    if (localStorage.getItem(WORKSPACE_SORT_STORAGE_KEY) === next) return;
    localStorage.setItem(WORKSPACE_SORT_STORAGE_KEY, next);
  } catch {
    // Quota / private mode — a lost sort preference is not worth throwing over.
  }
};
